// SPDX-FileCopyrightText: 2026 Mads Ringblom
//
// SPDX-License-Identifier: GPL-3.0-or-later

import { tokenVar } from '../../../bygning/lens';
import type { HierarchyRow } from '../../../bygning/overblik';
import { formatTonnes } from '../../../kortlaegning/vocab';
import { CIRC_EMPTY_TEXT, circularityAriaLabel } from '../../../overblik/model';
import { Panel } from '../Panel';
import styles from './Overblik.module.css';

/**
 * "Affaldshierarki · masse" (v8 `.bar`): one row per step, best first, the
 * track filled by the step's share of the tonnes in its --circ-* colour.
 * Widths are flex-grow by tonnes (no percentage literals); the list carries
 * the split in words, so colour is never the only carrier.
 */
export function HierarchyBars({ rows }: { rows: readonly HierarchyRow[] }) {
  const total = rows.reduce((a, r) => a + r.tonnes, 0);
  const segments = rows
    .filter((r) => r.tonnes > 0)
    .map((r) => ({ treatment: r.treatment, label: r.label, tonnes: r.tonnes, percent: r.percent }));
  return (
    <Panel title="Affaldshierarki · masse" fill>
      {total <= 0 ? (
        <p className={styles.muted}>{CIRC_EMPTY_TEXT}. Tons pr. type sættes i Ressourcer.</p>
      ) : (
        <ul className={styles.bars} aria-label={circularityAriaLabel(segments)}>
          {rows.map((r) => (
            <li key={r.treatment} className={styles.bar} title={`${r.label}: ${formatTonnes(r.tonnes)} (${r.percent} %)`}>
              <span className={styles.barLabel}>{r.label}</span>
              <span className={styles.track} aria-hidden="true">
                <i
                  className={r.tonnes > 0 ? styles.fill : `${styles.fill} ${styles.zero}`}
                  style={{ flexGrow: r.tonnes, background: tokenVar(r.token) }}
                />
                <i className={styles.rest} style={{ flexGrow: r.rest }} />
              </span>
              <b className={styles.barValue}>{formatTonnes(r.tonnes)}</b>
            </li>
          ))}
        </ul>
      )}
    </Panel>
  );
}
